"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { fetchBlogs } from "@/lib/publications/api";
import { BlogPost } from "@/lib/publications/types";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface SmallArticleCardProps {
  post: BlogPost;
  onClick?: () => void;
}

const SmallArticleCard = ({ post, onClick }: SmallArticleCardProps) => {
  return (
    <div className="self-stretch inline-flex justify-start items-start gap-4 cursor-pointer" onClick={onClick}>
      <div className="w-20 h-20 lg:w-24 lg:h-24 relative bg-slate-100 rounded-2xl overflow-hidden shrink-0">
        <img className="w-full h-full object-cover" src={post.heroImage} alt={post.title} />
      </div>
      <div className="flex-1 inline-flex flex-col justify-start items-start gap-2 min-w-0">
        <div className="self-stretch justify-start text-body-sm text-landing-secondary">
          {post.category} - {post.readTime} min read
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-1">
          <div className="self-stretch justify-start text-tag-md truncate text-landing-primary">{post.title}</div>
          <div className="self-stretch justify-start text-body landing-text line-clamp-2 text-landing-secondary">
            {post.excerpt}
          </div>
        </div>
      </div>
    </div>
  );
};

// Medium Article Card Component (for tips & tricks and team sections)
interface MediumArticleCardProps {
  post: BlogPost;
  onClick?: () => void;
}

const MediumArticleCard = ({ post, onClick }: MediumArticleCardProps) => {
  return (
    <div
      className="flex-1 inline-flex flex-col justify-start items-start gap-4 cursor-pointer min-w-0"
      onClick={onClick}
    >
      <div className="self-stretch h-40 lg:h-52 relative bg-slate-100 rounded-2xl overflow-hidden">
        <img className="w-full h-full object-cover" src={post.heroImage} alt={post.title} />
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <div className="self-stretch justify-start text-body-sm text-landing-secondary">
          {post.category} - {post.readTime} min read
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-1">
          <div className="self-stretch justify-start text-tag-lg line-clamp-2 text-landing-primary">{post.title}</div>
          <div className="self-stretch justify-start text-body landing-text line-clamp-3 text-landing-secondary">
            {post.excerpt}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Blog Component
export default function Blog() {
  const router = useRouter();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch blog posts on component mount
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const blogs = await fetchBlogs();
        setBlogPosts(blogs);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load blog posts");
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const handlePostClick = (post: BlogPost) => {
    router.push(`/blogs/${post.slug}`);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-landing-bg-primary" data-section="blog">
        <div className="w-full flex justify-center items-center py-24">
          <div className="container-page flex flex-col gap-16 pt-8">
            {/* Hero Section Skeleton */}
            <div className="w-full flex flex-col justify-start items-start gap-12">
              <div className="h-16 w-80 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* Main Content Section Skeleton */}
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="w-full flex flex-col lg:flex-row justify-start items-start gap-8 lg:gap-6">
                <div className="w-full lg:w-[630px] h-64 lg:h-80 bg-gray-200 rounded-2xl animate-pulse" />
                <div className="w-full lg:flex-1 flex flex-col gap-6">
                  <div className="h-20 lg:h-24 bg-gray-200 rounded-2xl animate-pulse" />
                  <div className="h-20 lg:h-24 bg-gray-200 rounded-2xl animate-pulse" />
                  <div className="h-20 lg:h-24 bg-gray-200 rounded-2xl animate-pulse" />
                </div>
              </div>
            </div>

            {/* Tips & Tricks Section Skeleton */}
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="h-8 w-36 bg-gray-200 rounded animate-pulse" />
                <div className="self-stretch h-px relative bg-gray-200" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
              </div>
            </div>

            {/* Team Section Skeleton */}
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="self-stretch h-px relative bg-gray-200" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
                <div className="h-40 lg:h-52 bg-gray-200 rounded-2xl animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen container-page bg-landing-bg-primary">
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="text-lg text-red-600">Error: {error}</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const listedPosts = blogPosts.filter((p) => !p.unlisted);

  // Smart hero logic: prioritize featured items but always fill 4 slots (1 main + 3 small)
  const getHeroPosts = () => {
    const featured = listedPosts.filter((post) => post.featured);
    const nonFeatured = listedPosts.filter((post) => !post.featured);

    if (featured.length >= 4) {
      // More than 4 featured: take top 4 featured
      return featured.slice(0, 4);
    } else {
      // Less than 4 featured: fill remaining slots with non-featured
      const needed = 4 - featured.length;
      return [...featured, ...nonFeatured.slice(0, needed)];
    }
  };

  const heroPosts = getHeroPosts();
  const heroMainPost = heroPosts[0]; // Main large article
  const heroSmallPosts = heroPosts.slice(1, 4); // 3 small articles

  // Group ALL posts by category (don't exclude hero posts from category sections)
  const postsByCategory = listedPosts.reduce(
    (acc, post) => {
      if (!acc[post.category]) {
        acc[post.category] = [];
      }
      acc[post.category].push(post);
      return acc;
    },
    {} as Record<string, typeof listedPosts>
  );

  // Get categories that actually have posts (sorted alphabetically for consistency)
  const categoriesWithPosts = Object.keys(postsByCategory).sort();

  return (
    <div className="min-h-screen overflow-x-hidden bg-landing-bg-primary" data-section="blog">
      <div className="w-full flex justify-center items-center py-24">
        <div className="container-page flex flex-col gap-16 pt-8">
          {/* Hero Section */}
          <div className="w-full flex flex-col justify-start items-start gap-12">
            <div className="self-stretch justify-start text-h1 text-landing-primary">The Nowadays blog</div>
          </div>

          {/* Main Content Section */}
          <div className="self-stretch flex flex-col justify-start items-start gap-6">
            <div className="w-full flex flex-col lg:flex-row justify-start items-start gap-8 lg:gap-6">
              {/* Large article on left */}
              {heroMainPost && (
                <div className="w-full lg:w-[630px] shrink-0 inline-flex flex-col justify-start items-start gap-4">
                  <div
                    className="self-stretch h-64 lg:h-80 relative bg-slate-100 rounded-2xl overflow-hidden cursor-pointer"
                    onClick={() => handlePostClick(heroMainPost)}
                  >
                    <img className="w-full h-full object-cover" src={heroMainPost.heroImage} alt={heroMainPost.title} />
                  </div>
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch justify-start text-body-sm text-landing-secondary">
                      {heroMainPost.category} - {heroMainPost.readTime} min read
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <div
                        className="self-stretch justify-start text-tag-lg cursor-pointer text-landing-primary"
                        onClick={() => handlePostClick(heroMainPost)}
                      >
                        {heroMainPost.title}
                      </div>
                      <div className="self-stretch justify-start text-body text-landing-secondary">
                        {heroMainPost.excerpt}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Small articles on right */}
              <div className="w-full lg:flex-1 inline-flex flex-col justify-center items-start gap-6">
                {heroSmallPosts.map((post) => (
                  <SmallArticleCard key={post.id} post={post} onClick={() => handlePostClick(post)} />
                ))}
              </div>
            </div>
          </div>

          {/* Dynamic Category Sections */}
          {categoriesWithPosts.map((category) => (
            <div key={category} className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4">
                  <div className="justify-start text-h3 text-landing-primary">{category}</div>
                  {/* <div className="flex justify-start items-start gap-4">
                    <Button variant="ghost" size="md">
                      Read more
                    </Button>
                  </div> */}
                </div>
                <div className="self-stretch h-px relative bg-gray-200" />
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                {postsByCategory[category].map((post) => (
                  <MediumArticleCard key={post.id} post={post} onClick={() => handlePostClick(post)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
